import type { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import type { DirectReportsType, EmploymentTypes, WorkArrangements, WorkExperienceType } from '@talentmesh/utils';
import { z } from 'zod';
import { JobPostingType as JobPostingTypes } from '@/DTO/Assessments/JobPostingType';
import { JobAdLanguage } from '@/Models/JobAdLanguage';
import LocationDetails from '@/Models/LocationDetails';
import { isNullOrWhiteSpace } from '@/Utils/Helpers';
import { defaultLanguage } from '@/Utils/JobAdLanguage/JobAdLanguages';
import ValidationErrors from '@/Utils/ValidationErrors';

const MAX_SALARY = 9999999999999998; // this is maximum number allowed by eslint

const JOB_TITLE_MAX_LENGTH = 50;
const JOB_TITLE_MAX_LENGTH_ERROR = `Job title should not exceed ${JOB_TITLE_MAX_LENGTH} characters`;

const jobAdLanguageSchema = z.object({
    id: z.string().min(1, { message: ValidationErrors.Required }),
    label: z.string(),
});

const currencySchema = z.object({
    id: z.string(),
    label: z.string(),
});

const parseSalaryValue = (value: string | number | undefined): number | undefined => {
    if (value === undefined || value === '') {
        return undefined;
    }

    const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));

    return Number.isNaN(parsed) ? undefined : parsed;
};

const addSalaryFieldIssues = (
    ctx: z.RefinementCtx,
    field: 'yearlySalaryFrom' | 'yearlySalaryTo',
    value: string | number | undefined,
    requiredMessage: string,
) => {
    const parsed = parseSalaryValue(value);

    if (parsed === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: value === '' || value === undefined ? requiredMessage : ValidationErrors.InvalidInputError,
            path: [field],
        });
        return undefined;
    }

    if (parsed <= 0) {
        ctx.addIssue({
            code: 'custom',
            message: ValidationErrors.NegativeValueError,
            path: [field],
        });
    }

    if (parsed > MAX_SALARY) {
        ctx.addIssue({
            code: 'custom',
            message: ValidationErrors.SalaryExceedsSystemLimitError,
            path: [field],
        });
    }

    return parsed;
};

export const jobDetailsSchema = z
    .object({
        assessmentName: z
            .string()
            .min(1, { message: ValidationErrors.Required })
            .max(JOB_TITLE_MAX_LENGTH, { message: JOB_TITLE_MAX_LENGTH_ERROR }),
        workArrangement: z.custom<WorkArrangements>(),
        jobLocation: z
            .custom<LocationDetails | null>()
            .nullable()
            .refine((value) => value !== null, { message: ValidationErrors.Required }),
        jobFunctionId: z.string().min(1, { message: ValidationErrors.Required }),
        jobCategoryId: z.string().min(1, { message: ValidationErrors.Required }),
        workExperience: z.custom<WorkExperienceType>(),
        employmentType: z.custom<EmploymentTypes>(),
        directReports: z.custom<DirectReportsType>(),
        doNotDisplaySalary: z.boolean(),
        yearlySalaryFrom: z.preprocess((value) => (value === undefined || value === null ? '' : value), z.string()),
        yearlySalaryTo: z.preprocess((value) => (value === undefined || value === null ? '' : value), z.string()),
        currency: currencySchema,
        jobDescriptionLanguage: jobAdLanguageSchema,
        jobPostingType: z.custom<JobPostingTypes>(),
    })
    .superRefine((data, ctx) => {
        if (data.doNotDisplaySalary) {
            return;
        }

        const yearlySalaryFrom = addSalaryFieldIssues(
            ctx,
            'yearlySalaryFrom',
            data.yearlySalaryFrom,
            ValidationErrors.EmptyFieldError,
        );
        const yearlySalaryTo = addSalaryFieldIssues(
            ctx,
            'yearlySalaryTo',
            data.yearlySalaryTo,
            ValidationErrors.EmptyFieldError,
        );

        if (yearlySalaryFrom !== undefined && yearlySalaryTo !== undefined && yearlySalaryTo < yearlySalaryFrom) {
            ctx.addIssue({
                code: 'custom',
                message: ValidationErrors.IncorrectSalaryLimitsError,
                path: ['yearlySalaryTo'],
            });
        }

        if (isNullOrWhiteSpace(data.currency.id)) {
            ctx.addIssue({
                code: 'custom',
                message: ValidationErrors.EmptyFieldError,
                path: ['currency', 'id'],
            });
        }
    });

export interface JobDetailsFormValues {
    assessmentName: string;
    workArrangement: WorkArrangements;
    jobLocation: LocationDetails | null;
    jobFunctionId: string;
    jobCategoryId: string;
    workExperience: WorkExperienceType;
    employmentType: EmploymentTypes;
    directReports: DirectReportsType;
    yearlySalaryFrom: string;
    yearlySalaryTo: string;
    currency: AutocompleteOption;
    doNotDisplaySalary: boolean;
    jobDescriptionLanguage: JobAdLanguage;
    jobPostingType: JobPostingTypes;
}

export const defaultJobDetailsFormValues: JobDetailsFormValues = {
    assessmentName: '',
    workArrangement: 'OnLocation',
    jobLocation: null,
    jobCategoryId: '',
    jobFunctionId: '',
    workExperience: 'GraduateOrAnalyst',
    employmentType: 'FullTime',
    directReports: 'None',
    yearlySalaryFrom: '',
    yearlySalaryTo: '',
    currency: { id: '', label: '' },
    doNotDisplaySalary: false,
    jobDescriptionLanguage: defaultLanguage,
    jobPostingType: 'Direct',
};
